import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { FaTimes } from "react-icons/fa";
import { AddPackageModalProps } from "../../Types/types";
import { uploadZipFile } from "../Manual_List/fetch/slices";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export const AddPackageModal: React.FC<AddPackageModalProps> = ({
  isOpen,
  onClose,
  translations,
  values,
  setValues,
  onClick,
}) => {
  const dispatch = useDispatch();
  const [localValues, setLocalValues] = useState(values);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalValues(values);
    }
  }, [isOpen, values]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.name.endsWith(".zip")) {
      setFile(selectedFile);
      setFileError("");
    } else {
      setFileError(translations.invalidZipFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const selectedFile = e.dataTransfer.files?.[0];
    if (selectedFile && selectedFile.name.endsWith(".zip")) {
      setFile(selectedFile);
      setFileError("");
    } else {
      setFileError(translations.invalidZipFile);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleSave = () => {
    setValues(localValues);

    if (file) {
      setIsUploading(true);
      dispatch(uploadZipFile(file) as any)
        .then((response: any) => {
          setIsUploading(false);
          if (response.error) {
            toast.error(translations.uploadFailed);
          } else {
            toast.success(translations.uploadSuccess);
            onClose();
          }
        })
        .catch((error: unknown) => {
          setIsUploading(false);
          console.error("Upload error:", error);
          toast.error(translations.uploadFailed);
        });
    } else {
      setFileError(translations.uploadBeforeSave);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-[#586EDFCC]">
      <div className="bg-white rounded-lg shadow-lg p-4 w-full max-w-7xl relative mx-2 sm:mx-auto dark:bg-darkSecondaryBackground">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500"
        >
          <FaTimes />
        </button>
        <div className="flex justify-center items-center mb-4">
          <h2 className="text-lg font-semibold">{translations.addPackage}</h2>
        </div>
        <form className="space-y-3">
          <div className="mb-4">
            <label htmlFor="fileUpload" className="block mb-1 text-base ml-2">
              {translations.uploadZipFile}
            </label>
            <div
              className="w-full p-6 border border-dashed rounded-md shadow-sm dark:bg-darkSecondaryBackground dark:border-purple"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="fileUpload"
                className="hidden"
                accept=".zip"
                onChange={handleFileChange}
              />
              {file ? (
                <div className="relative w-full">
                  <p className="text-center">{file.name}</p>
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2"
                    onClick={handleRemoveFile}
                  >
                    <FaTimes className="text-red-500" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="fileUpload"
                  className="w-full h-full flex items-center justify-center cursor-pointer"
                >
                  <p className="text-center">{translations.dragDropOrClick}</p>
                </label>
              )}
            </div>
            {fileError && (
              <span className="text-red-500 font-semibold text-sm">
                {fileError}
              </span>
            )}
          </div>
        </form>
        {isUploading && (
          <div className="flex justify-center items-center mt-4">
            <CircularProgress />
          </div>
        )}
        <div className="flex justify-between space-x-2 mt-4 sm:mt-6">
          <button
            type="button"
            className="bg-purple text-white px-3 py-2 rounded font-medium shadow-sm"
            onClick={onClick}
            disabled={isUploading}
          >
            {translations.addIndividually}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-3 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
              onClick={onClose}
              disabled={isUploading}
            >
              {translations.discard}
            </button>
            <button
              type="button"
              className="bg-purple text-white px-3 py-2 rounded font-medium shadow-sm"
              onClick={handleSave}
              disabled={isUploading}
            >
              {translations.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
